import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { RootState } from 'store';
import {
  getContactRequest,
  selectContactsLoading,
  deleteContactRequest,
  clearContactState,
} from 'store/contacts';
import { ArrowLeft, Copy, Check, Edit2, Trash2 } from 'lucide-react';
import Button from 'components/ui/Button';
import Overview from 'components/contacts/Tabs/Overview';
import AttributesTab from 'components/contacts/Tabs/AttributesTab';
import SegmentsTab from 'components/contacts/Tabs/SegmentsTab';
import ActivityTab from 'components/contacts/Tabs/ActivityTab';
import ContactEmailsTab from 'components/contacts/Tabs/ContactEmailsTab';
import { IContact } from 'types';
import { formatDate } from 'utils/helper';
import { useToast } from 'contexts/ToastContext';

const ContactsDetails = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const contact = useSelector(
    (state: RootState) => state.contacts.selectedContact
  );
  const loading = useSelector(selectContactsLoading);
  const error = useSelector((state: RootState) => state.contacts.error);
  const [activeTab, setActiveTab] = useState('Overview');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [contactWasLoaded, setContactWasLoaded] = useState(false);
  const [copiedId, setCopiedId] = useState(false);

  const tabs = [
    'Overview',
    'Attributes',
    'Segments',
    'Drafts',
    'Sent',
    'Failed',
    'Activity',
  ];

  useEffect(() => {
    if (id) {
      dispatch(getContactRequest(id));
    }
  }, [id, dispatch]);

  // Track when contact is successfully loaded
  useEffect(() => {
    if (!loading && contact) {
      setContactWasLoaded(true);
    }
  }, [contact, loading]);

  // Handle delete success - navigate back when contact is deleted
  useEffect(() => {
    if (!loading && !contact && id && contactWasLoaded) {
      // Contact was deleted, navigate back to contacts list
      showToast({
        type: 'success',
        title: 'Contact Deleted',
        message: 'Contact has been deleted successfully.',
      });
      navigate('/contacts');
    }
  }, [contact, loading, id, navigate, showToast, contactWasLoaded]);

  // Handle errors
  useEffect(() => {
    if (error) {
      showToast({
        type: 'error',
        title: 'Error',
        message: error,
      });
      dispatch(clearContactState());
    }
  }, [error, showToast, dispatch]);

  const handleEditContact = () => {
    if (contact?._id) {
      navigate(`/contacts/edit/${contact._id}`);
    }
  };

  const handleDeleteContact = () => {
    if (id) {
      dispatch(deleteContactRequest(id));
      setShowDeleteConfirm(false);
    }
  };

  const handleCopyContactId = async () => {
    if (!contact?._id) return;
    try {
      await navigator.clipboard.writeText(contact._id);
      setCopiedId(true);
      setTimeout(() => setCopiedId(false), 1500);
    } catch {
      // ignore
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'Overview':
        return <Overview contact={contact as IContact} />;
      case 'Attributes':
        return <AttributesTab contact={contact as IContact} />;
      case 'Relationships':
        return <div>Relationships</div>;
      case 'Segments':
        return <SegmentsTab contactId={id || ''} />;
      case 'Devices':
        return <div>Devices</div>;
      case 'Drafts':
        return <ContactEmailsTab contactId={id || ''} status="draft" />;
      case 'Sent':
        return <ContactEmailsTab contactId={id || ''} status="sent" />;
      case 'Failed':
        return <ContactEmailsTab contactId={id || ''} status="failed" />;
      case 'Activity':
        return <ActivityTab contactId={id || ''} />;
      case 'Journeys':
        return <div>Journeys</div>;
    }
  };

  if (loading) {
    return (
      <div className="p-6 space-y-4 bg-background">
        {/* Page Title Skeleton */}
        <div className="flex items-center justify-between">
          <div className="h-10 bg-gray-200 rounded animate-pulse w-32"></div>
        </div>

        {/* Contact Card Skeleton */}
        <div className="w-full flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-gray-200 rounded-full animate-pulse"></div>
            <div className="space-y-3">
              <div className="h-6 bg-gray-200 rounded animate-pulse w-48"></div>
              <div className="h-4 bg-gray-200 rounded animate-pulse w-64"></div>
              <div className="flex gap-4">
                <div className="h-4 bg-gray-200 rounded animate-pulse w-32"></div>
                <div className="h-4 bg-gray-200 rounded animate-pulse w-32"></div>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs Skeleton */}
        <div className="w-full">
          <div className="flex space-x-8 border-b border-gray-200 pb-3">
            {tabs.map((tab) => (
              <div
                key={tab}
                className="h-10 bg-gray-200 rounded animate-pulse w-20"
              ></div>
            ))}
          </div>
        </div>

        {/* Content Skeleton */}
        <div className="mt-10">
          <div className="h-96 bg-gray-200 rounded animate-pulse"></div>
        </div>
      </div>
    );
  }

  if (!loading && !contact) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <Button
            variant="ghost"
            size="medium"
            onClick={() => navigate(-1)}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </Button>
        </div>
        <div className="text-center py-12">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Contact Not Found
          </h2>
          <p className="text-gray-600 mb-4">
            The contact you're looking for doesn't exist.
          </p>
          <Button onClick={() => navigate('/contacts')} variant="outline">
            Back to Contacts
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-4 bg-background">
      {/* Contact Card */}
      <div className="w-full flex flex-col gap-4 pb-2 lg:flex-row lg:items-center lg:justify-between">
        {/* Avatar & Info */}
        <div className="flex items-center gap-4">
          <div
            className="w-12 h-12 shrink-0 rounded-full bg-primary/90
      flex items-center justify-center text-primary-foreground
      text-lg font-semibold"
          >
            {contact?.firstName?.[0] || contact?.email?.[0] || 'N/A'}
          </div>

          <div className="min-w-0">
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-lg font-semibold text-foreground truncate">
                {contact?.firstName && contact?.lastName
                  ? `${contact.firstName} ${contact.lastName}`
                  : contact?.firstName ||
                    contact?.lastName ||
                    contact?.email ||
                    'N/A'}
              </h1>

              {/* Status badge */}
              {contact?.unsubscribed ? (
                <span
                  className="inline-flex items-center gap-1 rounded-full
            bg-destructive/10 text-destructive text-xs font-medium px-2 py-0.5"
                >
                  ● Unsubscribed
                </span>
              ) : (
                <span
                  className="inline-flex items-center gap-1 rounded-full
            bg-success/10 text-success text-xs font-medium px-2 py-0.5"
                >
                  ● Subscribed
                </span>
              )}
            </div>

            {contact?.email && (contact?.firstName || contact?.lastName) && (
              <p className="text-sm text-muted-foreground truncate mt-0.5">
                {contact.email}
              </p>
            )}

            <div className="flex items-center gap-6 text-sm text-muted-foreground mt-2">
              <div>
                <span className="block text-xs uppercase tracking-wide">
                  Signed up
                </span>
                <span className="text-foreground">
                  {formatDate(contact?.createdAt)}
                </span>
              </div>
              {contact?._id && (
                <div className="flex items-center gap-1.5">
                  <span className="block text-xs uppercase tracking-wide">
                    Contact ID
                  </span>
                  <span className="inline-flex items-center gap-1.5 text-foreground font-mono text-xs">
                    <span className="truncate max-w-[140px] sm:max-w-[200px]">
                      {contact._id}
                    </span>
                    <button
                      type="button"
                      onClick={handleCopyContactId}
                      className="shrink-0 p-1 rounded hover:bg-muted transition-colors focus:outline-none focus:ring-0"
                      aria-label="Copy contact ID"
                    >
                      {copiedId ? (
                        <Check
                          className="w-3.5 h-3.5 text-success animate-copy-done"
                          aria-hidden
                        />
                      ) : (
                        <Copy
                          className="w-3.5 h-3.5 text-muted-foreground hover:text-foreground transition-colors"
                          aria-hidden
                        />
                      )}
                    </button>
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 justify-end">
          <Button
            variant="outline"
            size="small"
            onClick={handleEditContact}
            className="flex items-center gap-2
        border-success/30 hover:border-success/40
        hover:bg-success/10 text-foreground"
          >
            <Edit2 className="w-4 h-4 text-success" />
            Edit
          </Button>

          <Button
            variant="outline"
            size="small"
            onClick={() => setShowDeleteConfirm(true)}
            className="flex items-center gap-2
        border-destructive/30 hover:border-destructive/40
        hover:bg-destructive/10 text-destructive"
          >
            <Trash2 className="w-4 h-4" />
            Delete
          </Button>
        </div>
      </div>

      <div className="w-full">
        <div className="inline-flex items-center gap-1 rounded-lg bg-muted/80 px-1.5 py-1 border border-border/50">
          {tabs.map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`
                px-3 py-1.5 rounded-md text-sm font-medium whitespace-nowrap
                transition-all duration-200
                ${
                  activeTab === tab
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground hover:bg-primary/10'
                }
              `}
            >
              {tab}
            </button>
          ))}
        </div>
      </div>
      <div className="mt-10">{renderTabContent()}</div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-foreground/50 flex items-center justify-center z-[9999]">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Delete Contact
            </h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete this contact? This action cannot
              be undone.
            </p>
            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => setShowDeleteConfirm(false)}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleDeleteContact}
                className="bg-destructive hover:bg-destructive-hover"
              >
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContactsDetails;
